// C:\IntegraNube_WebStore\src\pages\Marcas.js

import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  Chip,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import {
  ArrowForwardOutlined as ArrowForwardIcon,
  HomeOutlined as HomeIcon,
  Inventory2Outlined as InventoryIcon,
  RefreshOutlined as RefreshIcon,
  SellOutlined as BrandIcon,
} from '@mui/icons-material';

import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import api from '../api/api';

function cleanString(value) {
  const result = String(value ?? '').trim();
  return result || '';
}

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') return {};

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object' &&
    !Array.isArray(source.data)
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    !Array.isArray(source.data) &&
    (source.data.branding || source.data.nombre)
  ) {
    return source.data;
  }

  return source;
}

function safeColor(value, fallback) {
  return cleanString(value) || fallback;
}

function formatNumber(value, locale = 'es-CL') {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';

  try {
    return new Intl.NumberFormat(locale).format(number);
  } catch (_) {
    return Math.round(number).toLocaleString('es-CL');
  }
}

function extractBrands(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.marcas)) return payload.marcas;
  if (Array.isArray(payload?.data?.marcas)) return payload.data.marcas;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
}

function getBrandPath(marca) {
  const ruta = cleanString(marca?.ruta);
  if (ruta && ruta.startsWith('/')) return ruta;

  const slug = cleanString(marca?.slug);
  return slug
    ? `/marca/${encodeURIComponent(slug)}`
    : '/marcas';
}

function getBrandLogo(marca) {
  return cleanString(
    marca?.logo ||
    marca?.logo_url ||
    marca?.logoUrl ||
    marca?.imagen_logo ||
    marca?.imagenLogo
  );
}

function getBrandBanner(marca) {
  return cleanString(
    marca?.banner ||
    marca?.banner_url ||
    marca?.bannerUrl ||
    marca?.imagen ||
    marca?.imagen_url ||
    marca?.imagenUrl
  );
}

function getBrandDescription(marca) {
  return cleanString(
    marca?.descripcion ||
    marca?.descripcion_ecommerce ||
    marca?.descripcionEcommerce
  );
}

function normalizeBrands(items) {
  return (Array.isArray(items) ? items : [])
    .filter((marca) => {
      const nombre = cleanString(marca?.nombre);
      const slug = cleanString(marca?.slug);
      const ruta = cleanString(marca?.ruta);
      const productos = Number(marca?.productos || 0);

      return Boolean(nombre && (slug || ruta) && productos > 0);
    })
    .sort((a, b) => {
      const ordenA = Number(
        a?.orden ?? a?.orden_ecommerce ?? 0
      );
      const ordenB = Number(
        b?.orden ?? b?.orden_ecommerce ?? 0
      );

      if (ordenA !== ordenB) return ordenA - ordenB;

      const productosA = Number(a?.productos || 0);
      const productosB = Number(b?.productos || 0);
      if (productosA !== productosB) return productosB - productosA;

      return cleanString(a?.nombre).localeCompare(
        cleanString(b?.nombre),
        'es',
        { sensitivity: 'base' }
      );
    });
}

function BrandCard({
  marca,
  colorPrimario,
  colorSecundario,
  colorSuperficie,
  colorTexto,
  colorTextoSecundario,
  locale,
}) {
  const nombre = cleanString(marca?.nombre) || 'Marca';
  const descripcion = getBrandDescription(marca);
  const logo = getBrandLogo(marca);
  const banner = getBrandBanner(marca);
  const productos = Number(marca?.productos || 0);

  const [logoError, setLogoError] = useState(false);
  const [bannerError, setBannerError] = useState(false);

  useEffect(() => setLogoError(false), [logo]);
  useEffect(() => setBannerError(false), [banner]);

  const showBanner = Boolean(banner) && !bannerError;
  const showLogo = Boolean(logo) && !logoError;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        bgcolor: colorSuperficie,
        transition:
          'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha(colorPrimario, 0.35),
          boxShadow: '0 22px 50px rgba(15,23,42,0.10)',
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={getBrandPath(marca)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            minHeight: { xs: 190, sm: 205, md: 220 },
            overflow: 'hidden',
            background: `
              radial-gradient(circle at 15% 18%, ${alpha(
                colorPrimario,
                0.18
              )} 0, transparent 38%),
              radial-gradient(circle at 90% 0%, ${alpha(
                colorSecundario,
                0.12
              )} 0, transparent 34%),
              linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)
            `,
          }}
        >
          {showBanner ? (
            <Box
              component="img"
              src={banner}
              alt={`Banner ${nombre}`}
              loading="lazy"
              decoding="async"
              onError={() => setBannerError(true)}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.28,
              }}
            />
          ) : null}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              p: 3,
              background: showBanner
                ? 'linear-gradient(180deg, rgba(255,255,255,0.66), rgba(255,255,255,0.92))'
                : 'transparent',
            }}
          >
            {showLogo ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 230,
                  minHeight: 112,
                  display: 'grid',
                  placeItems: 'center',
                  px: 2.5,
                  py: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.96)',
                  border: '1px solid rgba(226,232,240,0.95)',
                  boxShadow: '0 14px 34px rgba(15,23,42,0.08)',
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt={`Logo ${nombre}`}
                  loading="lazy"
                  decoding="async"
                  onError={() => setLogoError(true)}
                  sx={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: 82,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            ) : (
              <Stack alignItems="center" spacing={1.25}>
                <BrandIcon
                  sx={{
                    fontSize: 66,
                    color: alpha(colorPrimario, 0.55),
                  }}
                />
                <Typography
                  sx={{
                    color: colorTexto,
                    fontSize: 21,
                    fontWeight: 900,
                    textAlign: 'center',
                  }}
                >
                  {nombre}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            p: 2.4,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
          >
            <Typography
              component="h2"
              sx={{
                color: colorTexto,
                fontSize: 19,
                fontWeight: 900,
                lineHeight: 1.25,
              }}
            >
              {nombre}
            </Typography>

            <Chip
              size="small"
              label={`${formatNumber(productos, locale)} productos`}
              sx={{
                flexShrink: 0,
                bgcolor: alpha(colorPrimario, 0.08),
                color: colorPrimario,
                fontWeight: 800,
              }}
            />
          </Stack>

          {descripcion ? (
            <Typography
              sx={{
                mt: 1.15,
                color: colorTextoSecundario,
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {descripcion}
            </Typography>
          ) : (
            <Typography
              sx={{
                mt: 1.15,
                color: colorTextoSecundario,
                lineHeight: 1.6,
              }}
            >
              Ver productos disponibles de {nombre}.
            </Typography>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              mt: 'auto',
              pt: 2,
              color: colorPrimario,
              fontWeight: 900,
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 14 }}>
              Ver marca
            </Typography>
            <ArrowForwardIcon fontSize="small" />
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
}

function BrandCardSkeleton() {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
      }}
    >
      <Skeleton variant="rectangular" height={220} />
      <Box sx={{ p: 2.4 }}>
        <Skeleton width="58%" height={32} />
        <Skeleton width="92%" />
        <Skeleton width="76%" />
      </Box>
    </Card>
  );
}

export default function Marcas({
  tienda = null,
  store = null,
  config = null,
  storeConfig = null,
}) {
  const tiendaData = useMemo(
    () => resolveTiendaData(tienda || store || config || storeConfig),
    [tienda, store, config, storeConfig]
  );

  const colorPrimario = safeColor(
    tiendaData?.branding?.colorPrimario,
    '#1976d2'
  );
  const colorSecundario = safeColor(
    tiendaData?.branding?.colorSecundario,
    '#111827'
  );
  const colorFondo = safeColor(
    tiendaData?.branding?.colorFondo,
    '#f8fafc'
  );
  const colorSuperficie = safeColor(
    tiendaData?.branding?.colorSuperficie,
    '#ffffff'
  );
  const colorTexto = safeColor(
    tiendaData?.branding?.colorTexto,
    '#111827'
  );
  const colorTextoSecundario = safeColor(
    tiendaData?.branding?.colorTextoSecundario,
    '#6b7280'
  );

  const locale =
    cleanString(tiendaData?.regionalizacion?.locale) ||
    cleanString(tiendaData?.locale) ||
    'es-CL';

  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    async function loadBrands() {
      setLoading(true);
      setError('');

      try {
        const response = await api.get('/marcas', {
          signal: controller.signal,
        });

        if (!mounted) return;

        setMarcas(
          normalizeBrands(extractBrands(response?.data))
        );
      } catch (requestError) {
        if (
          requestError?.name === 'CanceledError' ||
          requestError?.name === 'AbortError' ||
          requestError?.code === 'ERR_CANCELED'
        ) {
          return;
        }

        if (!mounted) return;

        setMarcas([]);
        setError(
          requestError?.response?.data?.message ||
          requestError?.message ||
          'No fue posible cargar las marcas.'
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadBrands();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [reloadKey]);

  const totalProductos = useMemo(
    () => marcas.reduce(
      (sum, marca) => sum + Number(marca?.productos || 0),
      0
    ),
    [marcas]
  );

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 80px)',
        bgcolor: colorFondo,
        color: colorTexto,
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ py: { xs: 2.5, md: 4.5 } }}
      >
        <Breadcrumbs sx={{ mb: 2.5 }}>
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            alignItems="center"
            spacing={0.65}
            sx={{
              color: colorTextoSecundario,
              textDecoration: 'none',
              '&:hover': { color: colorPrimario },
            }}
          >
            <HomeIcon fontSize="small" />
            <Typography component="span" sx={{ fontSize: 14 }}>
              Inicio
            </Typography>
          </Stack>

          <Typography sx={{ color: colorTexto, fontSize: 14 }}>
            Marcas
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: { xs: 3, md: 4 },
            px: { xs: 2.5, sm: 4, md: 5 },
            py: { xs: 3.5, md: 4.5 },
            mb: { xs: 3, md: 4 },
            bgcolor: colorSuperficie,
            border: '1px solid #e5e7eb',
            background: `
              radial-gradient(circle at 8% 0%, ${alpha(
                colorPrimario,
                0.14
              )} 0, transparent 36%),
              radial-gradient(circle at 92% 10%, ${alpha(
                colorSecundario,
                0.08
              )} 0, transparent 34%),
              ${colorSuperficie}
            `,
          }}
        >
          <BrandIcon
            sx={{
              position: 'absolute',
              right: { xs: -12, md: 28 },
              top: '50%',
              transform: 'translateY(-50%) rotate(-8deg)',
              fontSize: { xs: 120, md: 180 },
              color: alpha(colorPrimario, 0.08),
            }}
          />

          <Box sx={{ position: 'relative', maxWidth: 760 }}>
            <Chip
              icon={<BrandIcon />}
              label="Catálogo por marca"
              size="small"
              sx={{
                mb: 1.5,
                bgcolor: alpha(colorPrimario, 0.09),
                color: colorPrimario,
                fontWeight: 850,
              }}
            />

            <Typography
              component="h1"
              sx={{
                color: colorTexto,
                fontWeight: 950,
                letterSpacing: '-0.025em',
                lineHeight: 1.05,
                fontSize: { xs: 34, sm: 42, md: 50 },
              }}
            >
              Marcas
            </Typography>

            <Typography
              sx={{
                mt: 1.35,
                maxWidth: 680,
                color: colorTextoSecundario,
                lineHeight: 1.7,
                fontSize: { xs: 15, md: 17 },
              }}
            >
              Explore el catálogo por fabricante y acceda directamente a
              los productos disponibles de cada marca.
            </Typography>

            {!loading && !error ? (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.5, sm: 2 }}
                sx={{ mt: 2 }}
              >
                <Typography sx={{ fontWeight: 850, color: colorTexto }}>
                  {formatNumber(marcas.length, locale)} marcas
                </Typography>
                <Typography sx={{ color: colorTextoSecundario }}>
                  {formatNumber(totalProductos, locale)} productos asociados
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Box>

        {error ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => setReloadKey((value) => value + 1)}
              >
                Reintentar
              </Button>
            }
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <BrandCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : marcas.length > 0 ? (
          <Grid container spacing={2.5}>
            {marcas.map((marca) => (
              <Grid item xs={12} sm={6} md={4} key={marca.id}>
                <BrandCard
                  marca={marca}
                  colorPrimario={colorPrimario}
                  colorSecundario={colorSecundario}
                  colorSuperficie={colorSuperficie}
                  colorTexto={colorTexto}
                  colorTextoSecundario={colorTextoSecundario}
                  locale={locale}
                />
              </Grid>
            ))}
          </Grid>
        ) : !error ? (
          <Box
            sx={{
              py: 8,
              px: 2,
              textAlign: 'center',
              borderRadius: 4,
              bgcolor: colorSuperficie,
              border: '1px dashed #cbd5e1',
            }}
          >
            <InventoryIcon
              sx={{
                fontSize: 56,
                color: alpha(colorPrimario, 0.4),
                mb: 1.5,
              }}
            />
            <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
              No hay marcas publicadas
            </Typography>
            <Typography
              sx={{ mt: 0.75, color: colorTextoSecundario }}
            >
              Las marcas aparecerán aquí cuando tengan productos públicos
              asociados.
            </Typography>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
}
